<script setup lang="ts">
import type { Component } from 'vue'
import { computed, inject, onUnmounted, provide, ref } from 'vue'
import { ChevronDown, ChevronUp } from '@lucide/vue'
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { DSDropdownMenu, DSDropdownMenuContent, DSDropdownMenuLabel, DSDropdownMenuTrigger } from '@ds'
import { SIDEBAR_COLLAPSED, SIDEBAR_FLYOUT_HOLD_OPEN } from './const'
import SidebarItem from './SidebarItem.vue'

type Props = {
  icon?: Component
  label: string
  active?: boolean
  /** v-model — expanded-rail accordion open state. Ignored while collapsed. */
  expanded?: boolean
  badge?: number
  isMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  expanded: false,
})

const emit = defineEmits<{
  (e: 'update:expanded', value: boolean): void
  (e: 'trigger-click'): void
}>()

const collapsed = inject(SIDEBAR_COLLAPSED, ref(false))

const menuOpen = ref(false)

/**
 * Flyout opens after a short hover delay and closes after its own short delay, so the pointer
 * can travel from the trigger to the menu, and a quick sweep across triggers doesn't flash
 * every one of them open — the open only commits once the pointer settles on a trigger.
 * The dropdown must be non-modal (`:modal="false"`) to avoid pointer events being blocked and spurious mouseleave events.
 */
const MENU_OPEN_DELAY = 200
const MENU_CLOSE_DELAY = 150
let openTimeout: ReturnType<typeof setTimeout> | undefined
let closeTimeout: ReturnType<typeof setTimeout> | undefined
/** Count of open descendant overlays (nested menus, rename popovers) that must keep the flyout open. See `SIDEBAR_FLYOUT_HOLD_OPEN`. */
let holdCount = 0

const openMenu = () => {
  clearTimeout(closeTimeout)
  clearTimeout(openTimeout)
  if (menuOpen.value) return
  openTimeout = setTimeout(() => {
    menuOpen.value = true
  }, MENU_OPEN_DELAY)
}

const scheduleMenuClose = () => {
  clearTimeout(openTimeout)
  clearTimeout(closeTimeout)
  closeTimeout = setTimeout(() => {
    if (holdCount > 0) return
    menuOpen.value = false
  }, MENU_CLOSE_DELAY)
}

provide(SIDEBAR_FLYOUT_HOLD_OPEN, () => {
  clearTimeout(closeTimeout)
  holdCount++
  return () => {
    holdCount = Math.max(0, holdCount - 1)
    if (holdCount === 0) scheduleMenuClose()
  }
})

onUnmounted(() => {
  clearTimeout(openTimeout)
  clearTimeout(closeTimeout)
})

/** A real pointer click navigates to the group's last-opened item; a keyboard activation (detail === 0) just opens the flyout, as before. */
const handleTriggerClick = (event: MouseEvent) => {
  if (event.detail === 0) return
  clearTimeout(openTimeout)
  emit('trigger-click')
}

const BADGE_CLASS = 'size-4 rounded-full flex items-center justify-center bg-destructive text-[10px] font-normal text-destructive-foreground'

const triggerClasses = computed(() => cn(
  'relative transition-colors duration-ds-fast ease-ds-standard',
  props.active || menuOpen.value
    ? 'bg-white/15 text-primary-foreground'
    : 'text-primary-foreground/85 hover:bg-white/10 hover:text-primary-foreground',
))
</script>

<template>
  <DSDropdownMenu
    v-if="collapsed"
    v-model:open="menuOpen"
    :modal="false"
  >
    <DSDropdownMenuTrigger as-child>
      <SidebarItem
        :icon
        :label
        :active
        :class="triggerClasses"
        :is-mobile
        :data-refid="`sidebarGroup${label.replace(' ', '')}`"
        :tooltip="false"
        @mouseenter="openMenu"
        @mouseleave="scheduleMenuClose"
        @click="handleTriggerClick"
      >
        <template #badge>
          <div
            v-if="badge"
            :class="cn(BADGE_CLASS, 'absolute -top-0.5 -right-1.5')"
          >
            {{ badge }}
          </div>
        </template>
      </SidebarItem>
    </DSDropdownMenuTrigger>
    <DSDropdownMenuContent
      side="right"
      align="start"
      class="min-w-56"
      :side-offset="15"
      @mouseenter="openMenu"
      @mouseleave="scheduleMenuClose"
    >
      <DSDropdownMenuLabel>{{ label }}</DSDropdownMenuLabel>
      <slot name="menu" />
    </DSDropdownMenuContent>
  </DSDropdownMenu>

  <CollapsibleRoot
    v-else
    :open="expanded"
    class="flex flex-col gap-px"
    @update:open="emit('update:expanded', $event)"
  >
    <CollapsibleTrigger as-child>
      <SidebarItem
        :icon
        :label
        :is-mobile
        class="w-full"
        :class="active ? 'font-semibold' : ''"
      >
        <div
          v-if="badge"
          :class="BADGE_CLASS"
        >
          {{ badge }}
        </div>
        <component
          :is="expanded ? ChevronUp : ChevronDown"
          class="size-4 shrink-0 text-primary-foreground/60 ml-auto"
          aria-hidden="true"
        />
      </SidebarItem>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <slot name="children" />
    </CollapsibleContent>
  </CollapsibleRoot>
</template>
